import { DatePipe } from "@angular/common";
import {
    afterRenderEffect,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    input,
    OnInit,
    Output,
    Signal,
    signal,
    untracked,
    viewChild,
    ViewChild,
} from "@angular/core";
import { form, FormField } from "@angular/forms/signals";
import { TemporaryArea } from "@models/map/market-filter.model";
import { MinLengthPipe } from "@shared/pipes/minlength.pipe";
import { EvAccordionComponent } from "ev-accordion";
import { ClickOutsideDirective } from "ev-directives";
import { SortUtil } from "ev-grid-table";
import { EvLogoLoaderComponent } from "src/app/shared-components/ev-logo-loader/ev-logo-loader.component";
import { ILatLng } from "polydraw";
import { EvDropdownMenuComponent, ISearchDropDownItem } from "src/app/shared-components/ev-dropdown-menu/ev-dropdown-menu.component";
import { OptionsObject } from "src/app/shared-components/ev-dropdown/ev-dropdown.component";
import { FavoriteListDataService } from "src/services/estate/favorite-list-data.service";
import { MapMode } from "src/services/map/map-state.service";
import { FavoriteListStates } from "src/stores/overview-favorite-lists.store";
import { SelectedFavoriteListsStore } from "src/stores/selected-favorite-list.store";
import { EstateSaleItemComponent } from "../../estate-sale/estate-sales-list/estate-sale-item.component";
import * as lr from "./../../../../resources/list-sort-options.resources";
import { AreaListItemComponent } from "./area-list-item/area-list-item.component";
import { DrawnListItemComponent } from "./drawn-list-item/drawn-list-item.component";

@Component({
    selector: "favorite-list",
    templateUrl: "./favorite-list.component.html",
    styleUrl: "./favorite-list.component.scss",
    imports: [
        DatePipe,
        MinLengthPipe,
        EstateSaleItemComponent,
        EvAccordionComponent,
        AreaListItemComponent,
        DrawnListItemComponent,
        FormField,
        EvLogoLoaderComponent,
        ClickOutsideDirective,
        EvDropdownMenuComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoriteListComponent implements OnInit, AfterViewInit {
    hasSaveRights = input<boolean>(true);
    mapMode = input<MapMode>();
    @Output() onOpenMenu: EventEmitter<number> = new EventEmitter<number>();
    @Output() onCloseMenu: EventEmitter<number> = new EventEmitter<number>();
    @Output() onSave: EventEmitter<number> = new EventEmitter<number>();
    @Output() onClose: EventEmitter<number> = new EventEmitter<number>();
    @Output() onAddAllSelection: EventEmitter<number> = new EventEmitter<number>();

    readonly selectedFavoriteListStore = inject(SelectedFavoriteListsStore);
    readonly favoriteListDataService = inject(FavoriteListDataService);

    favoriteListModel = signal({
        name: this.selectedFavoriteListStore.name(),
    });
    favoriteListForm = form(this.favoriteListModel);
    showCancelInput = computed(() => !!this.favoriteListForm.name().value());
    private nameInput = viewChild<ElementRef<HTMLInputElement>>("nameInput");
    private nameHasFocus = false;
    private nameBeforeEdit = this.selectedFavoriteListStore.name();
    textAlignRight: boolean = false
    sortOptions: OptionsObject[] = [];
    FavoriteListStates = FavoriteListStates;
    areaIsChanged = computed(() => this.selectedFavoriteListStore.changed() && this.selectedFavoriteListStore.areasChanged());
    areaIsSaved = computed(() => this.selectedFavoriteListStore.areas()?.length > 0);
    polygonIsChanged = computed(() => this.selectedFavoriteListStore.changed() && this.selectedFavoriteListStore.polygonChanged());
    polygonIsSaved = computed(() => this.selectedFavoriteListStore.polygon()?.length > 0 && !this.polygonIsChanged());
    propertyIsChanged = computed(() => this.selectedFavoriteListStore.changed() && this.selectedFavoriteListStore.unitsChanged());
    propertyIsSaved = computed(() => this.selectedFavoriteListStore.sales()?.length > 0 && !this.propertyIsChanged());
    currentSortItem = computed(() => this.selectedFavoriteListStore.sortOrder());
    sortedSales = computed(() =>
        this.currentSortItem()?.key
            ? (this.selectedFavoriteListStore.sales() || []).sort(SortUtil.dynamicSort(this.currentSortItem()?.key))
            : this.selectedFavoriteListStore.sales() || [],
    );
    favoriteAreas = computed(() => this.selectedFavoriteListStore.areas() || []);
    favoritePolygons = computed(() => this.selectedFavoriteListStore.polygon() || []);
    nrOfAreas = computed(() => (this.favoriteAreas()?.length || 0) + (this.favoritePolygons()?.length || 0));
    emptyFavoriteList = computed(() => this.selectedFavoriteListStore.id() != -1 && this.selectedFavoriteListStore.isFavoriteListEmpty());
    favoriteListId = computed(() => this.selectedFavoriteListStore.id());
    sortOptionsWithSelected = computed(() => {
        if (this.selectedFavoriteListStore.sortOrder()) {
            const currentSortKey = this.selectedFavoriteListStore.sortOrder().key;
            return this.sortOptions.map((item) => ({
                ...item,
                isSelected: item.key === currentSortKey,
            }));
        }
        return this.sortOptions;
    });

    @ViewChild("searchResultDropDownCmp") searchResultDropDownCmp: any;
    selectedFilter = signal<string>("-sortDate");

    filterOptions: Signal<ISearchDropDownItem[]> = computed(() => {
        return [
            {
                key: "sortDate",
                maintext: "Dato lav-høy",
                isSelected: false,
            },
            {
                key: "-sortDate",
                maintext: "Dato høy-lav",
                isSelected: true,
            },
            {
                key: "address",
                maintext: "Adresse A-Å",
                isSelected: false,
            },
            {
                key: "-address",
                maintext: "Adresse Å-A",
                isSelected: false,
            },
            {
                key: "residentialMeasuredArea",
                maintext: "Areal lav-høy",
                isSelected: false,
            },
            {
                key: "-residentialMeasuredArea",
                maintext: "Areal høy-lav",
                isSelected: false,
            },
            {
                key: "price",
                maintext: "Pris lav-høy",
                isSelected: false,
            },
            {
                key: "-price",
                maintext: "Pris høy-lav",
                isSelected: false,
            },
        ];
    });

    constructor() {
        // Store name before editing, so we can check if the user actually changed it or not
        effect(() => {
            if (!this.selectedFavoriteListStore.editName()) return;
            // Untracked: only the opening should trigger the sync, otherwise we overwrite
            // what the user is typing.
            const name = untracked(() => this.selectedFavoriteListStore.name());
            this.nameBeforeEdit = name;
            this.favoriteListForm().reset({ name });
        });

        // Mark entire title when triggering change title from modal
        afterRenderEffect(() => {
            if (!this.selectedFavoriteListStore.editName()) return;
            const input = this.nameInput()?.nativeElement;
            // Only the first render after opening: if the field already has focus, we would
            // yank the selection back every time something else triggered a render.
            if (!input || input === document.activeElement) return;
            input.focus();
            input.select();
        });
    }

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        this.setInitialSortItem();
    }

    handleOnSelectedItemChange(sortItem: ISearchDropDownItem): void {
        if (sortItem !== null && sortItem?.key !== this.currentSortItem()?.key) {
            this.selectedFilter.set(sortItem.key as string);
            this.selectedFavoriteListStore.updateSortOrder(sortItem);
            sessionStorage.setItem("favouriteListCurrentSortItem", JSON.stringify(sortItem));
        }
    }

    removePolygon(polygon: ILatLng[][]): void {
        this.favoriteListDataService.removePolygon(polygon);
    }

    removeArea(area: TemporaryArea): void {
        this.favoriteListDataService.removeArea(area);
    }

    // Saves what the user typed. Called from Enter, blur and a real outside click.
    updateName(): void {
        this.commitName(this.favoriteListForm().value().name);
    }

    private commitName(name: string): void {
        if (!this.selectedFavoriteListStore.editName()) return;
        this.nameHasFocus = false;
        this.favoriteListForm().reset({ name });
        if (name === this.nameBeforeEdit) {
            this.selectedFavoriteListStore.updateEditName(false);
            return;
        }

        this.nameBeforeEdit = name;
        this.favoriteListDataService.updateFavoriteListName(name);
    }

    onNameFocus(): void {
        this.nameHasFocus = true;
    }


    onNameClickOutside(): void {
        if (this.nameHasFocus) return;
        this.updateName();
    }

    onSaveList(): void {
        if (!this.selectedFavoriteListStore.canSave()) return;
        this.onSave.emit(this.selectedFavoriteListStore.id());
    }

    // Cancel: back to the title we started editing with.
    resetText(): void {
        this.commitName(this.nameBeforeEdit);
    }

    private setInitialSortItem(): void {
        const storedOption = sessionStorage.getItem("favouriteListCurrentSortItem");
        const defaultSortOrderIndex = lr.getDefaultSortOrderIndex(this.mapMode());

        if (this.selectedFavoriteListStore.sortOrder()) return;

        if (storedOption) {
            const parsedOption = JSON.parse(storedOption);
            if (lr.isKeyInList(lr.ListOrigin.FavouriteList, this.mapMode(), parsedOption)) {
                this.selectedFavoriteListStore.updateSortOrder(parsedOption);
                return;
            }
        }

        // this.selectedFavoriteListStore.updateSortOrder(this.filterOptions()[defaultSortOrderIndex]);
    }
}
